import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Divider, useTheme } from '@mui/material';
import {
  APP_BAR_HEIGHT,
  CollapsedUserMenu,
  Navigation,
  NavRoute,
  NavWithSubRoutes,
  Sidebar,
  SidebarHeader,
  SidebarUserMenu,
} from '@shared/components';
import { useSidebarDisclosure } from '@shared/stores';
import { useAuthContext } from '@retail/auth/data-access';
import { useFetchMe } from '@retail/users/data-access';
import { useSharedTranslation } from '@shared/i18n';
import {
  RetailCollapsedSidebarHeader,
  RetailExpandedSidebarHeader,
  RetailExpandedUserMenu,
  RetailNavItem,
  RetailNavItemWithSubRoutes,
} from '@retail/components';
import { useActionsNavRoutes } from './useActionsNavRoutes';
import { useWindowSize } from '@uidotdev/usehooks';
import { useLocation } from 'react-router';

export type Route = NavRoute | NavWithSubRoutes;
export const APP_BAR_MGP_MARGIN_TOP = 16;

interface Props {
  navRoutes: Route[];
}

export function AppSidebar({ navRoutes }: Props) {
  const { t: sharedT } = useSharedTranslation();

  const location = useLocation();
  const { breakpoints } = useTheme();
  const { width: screenWidth } = useWindowSize();

  const largeBreakpoint = breakpoints.values['xl'];
  const isSmallerThanXL = screenWidth && screenWidth <= largeBreakpoint;
  const isLargerThanXL = screenWidth && screenWidth > largeBreakpoint;

  const actionsNavRoutes = useActionsNavRoutes();

  const {
    isOpen: isSidebarOpen,
    onToggle: toggleSidebar,
    onOpen: openSidebar,
    onClose: closeSidebar,
  } = useSidebarDisclosure();

  const [wasManuallyToggled, setWasManuallyToggled] = useState<boolean>(false);

  const { data: user } = useFetchMe({ refetchOnWindowFocus: false });
  const userName = useMemo(() => `${user?.givenName} ${user?.familyName}`, [user]);

  const { logout } = useAuthContext();
  const logoutDefault = useCallback(() => logout(), [logout]);

  const handleToggleSidebar = useCallback(() => {
    toggleSidebar();
  }, [toggleSidebar]);

  useEffect(() => {
    if (isLargerThanXL) setWasManuallyToggled(false);
  }, [isLargerThanXL]);

  // Close sidebar on smaller screens when location changes, open sidebar on larger screens if sidebar is not manually toggled
  useEffect(() => {
    if (!location) return;
    if (isSmallerThanXL) closeSidebar();
    if (isLargerThanXL && !wasManuallyToggled) {
      openSidebar();
      setWasManuallyToggled(true);
    }
  }, [closeSidebar, isLargerThanXL, isSmallerThanXL, location, openSidebar, wasManuallyToggled]);

  return (
    <Sidebar
      isOpen={isSidebarOpen}
      toggleOpen={handleToggleSidebar}
      appBarHeight={APP_BAR_HEIGHT + APP_BAR_MGP_MARGIN_TOP}
      hasBorderTopRightRadius
    >
      <SidebarHeader
        title={sharedT('shared.navigation')}
        expandedView={isSidebarOpen}
        CustomExpandedSidebarHeader={RetailExpandedSidebarHeader}
        CustomCollapsedSidebarHeader={RetailCollapsedSidebarHeader}
      />

      <Navigation
        navRoutes={navRoutes}
        expandedView={isSidebarOpen}
        CustomNavItem={RetailNavItem}
        expandView={openSidebar}
        CustomNavItemWithSubRoutes={RetailNavItemWithSubRoutes}
      />

      <Divider />

      <Navigation
        navRoutes={actionsNavRoutes}
        expandedView={isSidebarOpen}
        CustomNavItem={RetailNavItem}
        expandView={openSidebar}
        CustomNavItemWithSubRoutes={RetailNavItemWithSubRoutes}
      />

      <Box marginTop="auto" display="flex" p={3} justifyContent="center">
        <SidebarUserMenu
          userName={userName}
          expandedView={isSidebarOpen}
          logOut={logoutDefault}
          UserMenuExpanded={RetailExpandedUserMenu}
          UserMenuCollapsed={CollapsedUserMenu}
        />
      </Box>
    </Sidebar>
  );
}
